import { Game } from "../../core/Game";
import { MaterialInstance } from "../../game/Materials";
import { handSpaceLeft } from "../../game/Person";
import { atStand, isSellable } from "../../game/stand";
import { Player } from "../entities/Player";
import { StandEntity } from "../entities/StandEntity";
import { projectPerson } from "../projection";
import { Progression } from "../singletons/Progression";
import { ShopInfo } from "../singletons/ShopInfo";

/**
 * The stand command surface: setting the table up, setting pieces out,
 * and taking them back. Each command validates through the shared
 * helpers in `game/stand.ts` against the live entities, then writes
 * onto them; a refusal logs and returns false. Selling is not a
 * command — customers do that, in the StreetSystem.
 */

function player(game: Game): Player {
  return game.entities.getSingleton(Player);
}

/**
 * Put the table out by the road. Done once, standing at the stand's
 * spot in the grass, after the story has offered it
 * (`progression.standUnlocked`); from then on the table is there, solid,
 * with its sign, and takes work.
 */
export function setUpStand(game: Game): boolean {
  const thePlayer = player(game);
  const shopInfo = game.entities.getSingleton(ShopInfo).info;
  const stand = game.entities.getSingleton(StandEntity);
  if (stand.setUp) {
    console.warn("The stand is already set up");
    return false;
  }
  if (!game.entities.getSingleton(Progression).standUnlocked) {
    console.warn("Nothing has suggested a stand yet");
    return false;
  }
  if (!atStand(shopInfo, thePlayer.cell)) {
    console.warn("Tried to set the stand up away from its spot");
    return false;
  }
  stand.setUp = true;
  game.dispatch("standChanged", {});
  return true;
}

/**
 * Sets pieces from the arms out on the stand, the way the truck's bed
 * loads: one per press, an armful with Shift. Only sellable pieces go
 * out — raw stock stays in hand. The chip never offers an unsellable
 * piece, but the command enforces both rules anyway because where and
 * what the stand sells is a game rule.
 */
export function setOutAtStand(
  game: Game,
  materials: ReadonlyArray<MaterialInstance>,
): boolean {
  const thePlayer = player(game);
  const shopInfo = game.entities.getSingleton(ShopInfo).info;
  if (!game.entities.getSingleton(StandEntity).setUp) {
    console.warn("There is no stand to set pieces out on yet");
    return false;
  }
  if (!atStand(shopInfo, thePlayer.cell)) {
    console.warn("Tried to set out pieces away from the stand");
    return false;
  }
  if (materials.length === 0) {
    console.warn("Tried to set out nothing");
    return false;
  }
  if (
    !materials.every((material) =>
      thePlayer.inventory.some((item) => item === material),
    )
  ) {
    console.warn("Tried to set out material not in inventory");
    return false;
  }
  if (!materials.every(isSellable)) {
    console.warn("Tried to set out something nobody would pay for");
    return false;
  }
  const stand = game.entities.getSingleton(StandEntity);
  stand.pieces = [...stand.pieces, ...materials];
  thePlayer.inventory = thePlayer.inventory.filter(
    (item) => !materials.includes(item),
  );
  game.dispatch("standChanged", {});
  game.dispatch("playerChanged", {});
  return true;
}

/**
 * Takes pieces back off the stand into the arms — the newest set out
 * first, as many as asked for, capped by what the hands can still hold.
 */
export function takeFromStand(game: Game, count = 1): boolean {
  const person = projectPerson(game);
  const shopInfo = game.entities.getSingleton(ShopInfo).info;
  if (!atStand(shopInfo, person.position)) {
    console.warn("Tried to take from the stand out of reach");
    return false;
  }
  const stand = game.entities.getSingleton(StandEntity);
  const taken = Math.min(count, stand.pieces.length, handSpaceLeft(person));
  if (taken <= 0) {
    console.warn("Tried to take from the stand with full hands");
    return false;
  }
  const kept = stand.pieces.slice(0, stand.pieces.length - taken);
  const returned = stand.pieces.slice(stand.pieces.length - taken);
  const thePlayer = player(game);
  stand.pieces = kept;
  thePlayer.inventory = [...thePlayer.inventory, ...returned];
  game.dispatch("standChanged", {});
  game.dispatch("playerChanged", {});
  return true;
}
